import React, { useState, useEffect, useMemo } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Checkbox from "aldoo-ra/Checkbox"
import { showNotification } from "aldoo-ra/Notification"
import { GetTickets, UpdateTicket, DeleteTickets } from "./api"
import {
  TrashIcon,
  ShieldExclamationIcon,
  AdjustmentsHorizontalIcon,
  EyeIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import { formatDate, getRelativeTimeString } from "./time-format" // Import the time formatting functions

export default function SpamTicketsDialog({
  isOpen,
  onClose,
  parentRefreshKey,
  onTicketsUpdated,
  onSelectTicket, // Add new prop for handling ticket selection/viewing
}) {
  const [selectedItems, setSelectedItems] = useState([])
  const [refreshKey, setRefreshKey] = useState(0)
  const [searchText, setSearchText] = useState("")
  const [dataSource, setDataSource] = useState([])
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // Reset selections when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedItems([])
      setSearchText("")
      // Refresh the data when dialog opens
      setRefreshKey((prev) => prev + 1)
    }
  }, [isOpen])

  // Refresh when parent's refresh key changes
  useEffect(() => {
    if (isOpen && parentRefreshKey) {
      setRefreshKey((prev) => prev + 1)
    }
  }, [parentRefreshKey, isOpen])

  // Handle view ticket action
  const handleView = (id) => {
    if (onSelectTicket) {
      onSelectTicket(id)
      onClose() // Close the dialog after selecting a ticket
    }
  }

  // Handle mark as not spam action
  const handleMarkAsNotSpam = async () => {
    if (selectedItems.length === 0) return

    try {
      // Process each ticket individually to update spam status
      const updatePromises = selectedItems.map((ticketId) =>
        UpdateTicket({ ticketId, isSpam: false })
      )

      const results = await Promise.all(updatePromises)

      // Check if any updates failed
      const hasErrors = results.some((res) => res.error)

      if (hasErrors) {
        showNotification({
          message: "Failed to update some tickets",
          className: "bg-admin_error text-white",
        })
      } else {
        showNotification({
          message: `${selectedItems.length} ${
            selectedItems.length === 1 ? "ticket" : "tickets"
          } marked as not spam`,
          className: "bg-admin_primary text-admin_text_inv",
        })

        setSelectedItems([])
        setRefreshKey((prev) => prev + 1)

        // Notify parent component to refresh its data
        if (onTicketsUpdated) {
          onTicketsUpdated()
        }
      }
    } catch (error) {
      showNotification({
        message: "An error occurred while updating tickets",
        className: "bg-admin_error text-white",
      })
    }
  }

  // Handle permanent delete action
  const handleDeleteForever = async () => {
    if (selectedItems.length === 0) return

    try {
      const response = await DeleteTickets(selectedItems, "hard")

      if (response.error) {
        showNotification({
          message: response.message || "Failed to permanently delete tickets",
          className: "bg-admin_error text-white",
        })
      } else {
        showNotification({
          message: `${selectedItems.length} ${
            selectedItems.length === 1 ? "ticket" : "tickets"
          } permanently deleted`,
          className: "bg-admin_primary text-admin_text_inv",
        })

        setSelectedItems([])
        setRefreshKey((prev) => prev + 1)

        // Notify parent component to refresh its data
        if (onTicketsUpdated) {
          onTicketsUpdated()
        }
      }
    } catch (error) {
      showNotification({
        message: "An error occurred while permanently deleting tickets",
        className: "bg-admin_error text-white",
      })
    }
  }

  // Navigate to spam protection page
  const handleNavigateToSpamProtection = () => {
    window.location.href = "/admin/spam-protection"
  }

  // Handle select all change
  const handleSelectAll = (items, isSelected) => {
    if (isSelected) {
      setSelectedItems(items.map((item) => item.ticketID))
    } else {
      setSelectedItems([])
    }
  }

  // Handle selecting all visible items
  const handleSelectAllVisible = () => {
    // Get current visible items based on current page items
    const currentVisibleItems = dataSource.slice(0, itemsPerPage)

    const allVisibleSelected = currentVisibleItems.every((item) =>
      selectedItems.includes(item.ticketID)
    )

    if (allVisibleSelected) {
      // Deselect all visible items
      const visibleIds = currentVisibleItems.map((item) => item.ticketID)
      setSelectedItems((prevSelected) =>
        prevSelected.filter((id) => !visibleIds.includes(id))
      )
    } else {
      // Select all visible items
      const visibleIds = currentVisibleItems.map((item) => item.ticketID)

      // Merge with existing selections (keeping any selected items that aren't visible)
      setSelectedItems((prevSelected) => {
        const newSelected = [...prevSelected]
        visibleIds.forEach((id) => {
          if (!newSelected.includes(id)) {
            newSelected.push(id)
          }
        })
        return newSelected
      })
    }
  }

  // Handle individual selection change
  const handleSelectItem = (itemId, isSelected) => {
    if (isSelected) {
      setSelectedItems((prev) => [...prev, itemId])
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== itemId))
    }
  }

  // Handle search value from the DataTable's internal search
  const handleSearchChange = (value) => {
    setSearchText(value)
  }

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage)
  }

  // Handle data loaded
  const handleDataLoaded = (loadedData) => {
    setDataSource(loadedData || [])
    return loadedData
  }

  // Determine if all visible items are selected
  const allVisibleSelected = useMemo(() => {
    const currentVisibleItems = dataSource.slice(0, itemsPerPage)
    return (
      currentVisibleItems.length > 0 &&
      currentVisibleItems.every((item) => selectedItems.includes(item.ticketID))
    )
  }, [dataSource, selectedItems, itemsPerPage])

  // Style overrides for the DataTable
  const styleOverrides = {
    searchInput: "w-full max-w-[300px] ml-auto",
    getRowStyle: (index, item) => {
      // Check if this item is selected
      if (item && selectedItems.includes(item.ticketID)) {
        return "bg-admin_primary/10 border-l-2 border-admin_primary" // Selected row with highlight
      }
    },
  }

  // Get request fields - set match to only show spam tickets
  const getRequestFields = () => {
    return {
      match: { isSpam: true },
    }
  }

  // Table columns
  const columns = [
    {
      id: "select",
      headerClassName: "w-[60px]",
      header: (value, items) => (
        <Checkbox
          variant="admin"
          isOn={items.length > 0 && selectedItems.length === items.length}
          onChange={(isSelected) => handleSelectAll(items, isSelected)}
        />
      ),
      render: (value, item) => (
        <Checkbox
          variant="admin"
          isOn={selectedItems.includes(item.ticketID)}
          onChange={(isSelected) => handleSelectItem(item.ticketID, isSelected)}
        />
      ),
    },
    {
      id: "ticketID",
      title: "Ticket #",
      headerClassName: "w-20",
      render: (_, item) => (
        <Typography variant="admin_body" className="my-0">
          {item.ticketID}
        </Typography>
      ),
    },
    {
      id: "timestamp",
      title: "Date Created",
      headerClassName: "w-24",
      render: (_, item) => (
        <div className="flex flex-col">
          <Typography
            variant="admin_body"
            className="text-admin_text whitespace-nowrap my-0"
          >
            {formatDate(item.timestamp)}
          </Typography>
          <Typography variant="admin_txt2" className="text-admin_text_secondary/70">
            {getRelativeTimeString(item.timestamp)}
          </Typography>
        </div>
      ),
    },
    {
      id: "summary",
      title: "Summary",
      headerClassName: "w-[35%]", // Adjusted width to make room for date column
      render: (_, item) => (
        <Typography variant="admin_body" className="truncate max-w-md my-0">
          {item.summary}
        </Typography>
      ),
    },
    {
      id: "author",
      title: "Email",
      headerClassName: "w-1/5",
      render: (_, item) => (
        <Typography variant="admin_body" className="truncate max-w-full my-0">
          {item.author}
        </Typography>
      ),
    },
    {
      id: "status",
      title: "Status",
      headerClassName: "w-[56px]",
      render: (_, item) => {
        const statusConfig = {
          open: {
            color: "text-admin_success bg-admin_success/5",
            label: "Open",
          },
          resolved: {
            color: "text-admin_error bg-admin_error/5",
            label: "Resolved",
          },
        }
        const { color, label } = statusConfig[item.status] || {
          color: "text-admin_text_secondary bg-admin_neutral",
          label: item.status,
        }
        return (
          <span
            className={twMerge(
              color,
              "capitalize px-2 py-1 rounded-md text-xs font-medium"
            )}
          >
            {label}
          </span>
        )
      },
    },
    // Add actions column with view button
    {
      id: "actions",
      title: "Actions",
      headerClassName: "w-[56px]",
      render: (_, item) => (
        <div className="flex space-x-1">
          <button
            className="p-1.5 text-admin_text hover:text-admin_primary transition-colors"
            onClick={() => handleView(item.ticketID)}
            title="View ticket details"
          >
            <EyeIcon className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ]

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin"
      className="md:absolute w-full sm:w-[500px] md:w-[95%] lg:max-w-[1500px] h-full md:h-auto md:min-h-[85%]"
      closeOnEscape
      title="Spam Tickets"
      headerContent={
        <Button
          variant="admin_secondary"
          className="flex items-center mr-12"
          onClick={handleNavigateToSpamProtection}
        >
          <AdjustmentsHorizontalIcon className="h-5 w-5 mr-2" />
          Open Spam Protection
        </Button>
      }
    >
      <div className="p-6">
        <div className="min-h-[500px]">
          <DataTable
            apiMethod={GetTickets}
            variant="admin_browser"
            preferredHeight="70vh"
          itemHeight="h-[60px]"
          searchField="summary author ticketID"
          localUserPreferencesKey="admin.customerSupport.spamTickets"
        renders={columns}
          refreshKey={refreshKey}
          sort={{ timestamp: -1 }}
          styleOverrides={styleOverrides}
          requestFields={getRequestFields()}
          noData={
            <Typography variant="admin_body">No spam tickets found</Typography>
          }
          onSearchChange={handleSearchChange}
          onDataLoaded={handleDataLoaded}
          onItemsPerPageChange={handleItemsPerPageChange}
          headerControls={
            <div className="flex items-center">
              {selectedItems.length > 0 && (
                <>
                  <Typography
                    variant="admin_txt1"
                    className="whitespace-nowrap mr-4"
                  >
                    {selectedItems.length}{" "}
                    {selectedItems.length === 1 ? "ticket" : "tickets"} selected
                  </Typography>

                  <Button
                    variant="admin_secondary"
                    onClick={() => setSelectedItems([])}
                    className="whitespace-nowrap mr-2 flex items-center"
                  >
                    <XMarkIcon className="h-5 w-5 mr-1" /> Deselect
                  </Button>

                  <Button
                    variant="admin_primary"
                    onClick={handleMarkAsNotSpam}
                    disabled={selectedItems.length === 0}
                    className="whitespace-nowrap mr-2 flex items-center"
                  >
                    <ShieldExclamationIcon className="h-5 w-5 mr-1" />
                    Mark as Not Spam
                  </Button>

                  <Button
                    variant="admin_primary_red"
                    onClick={handleDeleteForever}
                    disabled={selectedItems.length === 0}
                    className="whitespace-nowrap flex items-center"
                  >
                    <TrashIcon className="h-5 w-5 mr-1" />
                    Delete Forever
                  </Button>
                </>
              )}
            </div>
          }
          />
        </div>
      </div>
    </Modal>
  )
}
